import * as React from 'react';
const { useState, useCallback } = React;
import { confirmAction, cancelAction } from '../services/assistantApi';
import type { ActionProposal } from '../types/assistant';

interface AssistantActionCardProps {
  data: ActionProposal;
}

export const AssistantActionCard: React.FC<AssistantActionCardProps> = ({ data }) => {
  const [status, setStatus] = useState(data.status);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [needsSubscription, setNeedsSubscription] = useState(false);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      const res = await confirmAction(data.action_id) as Record<string, any>;
      const actionResult = res.result || {};

      if (actionResult.success) {
        setStatus('executed');
        setResult(actionResult.message || 'Action completed successfully');
        if (actionResult.redirect_url) {
          setRedirectUrl(actionResult.redirect_url);
        }
      } else if (actionResult.needs_subscription) {
        setStatus('failed');
        setNeedsSubscription(true);
        setResult(actionResult.message || 'You need an active subscription');
        if (actionResult.redirect_url) {
          setRedirectUrl(actionResult.redirect_url);
        }
      } else {
        setStatus('failed');
        setResult(actionResult.error || actionResult.message || 'Failed to execute action');
      }
    } catch {
      setStatus('failed');
      setResult('Failed to execute action');
    }
    setLoading(false);
  }, [data.action_id]);

  const handleCancel = useCallback(async () => {
    setLoading(true);
    try {
      await cancelAction(data.action_id);
      setStatus('cancelled');
    } catch {
      // ignore
    }
    setLoading(false);
  }, [data.action_id]);

  const cardStyle: React.CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 12,
    background: '#fff',
    marginBottom: 8,
  };

  const resultButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    padding: '6px 14px',
    fontSize: 12,
    fontFamily: 'inherit',
    color: '#fff',
    background: '#2C5282',
    border: 'none',
    borderRadius: 16,
    cursor: 'pointer',
    textDecoration: 'none',
  };

  const actionLabel = data.action_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const details = data.details || {};

  // Contextual button label per action type. Falls back to a sensible generic.
  const resultButtonLabel = (() => {
    if (needsSubscription) return 'View plans';
    switch (data.action_type) {
      case 'draft_job': return 'Go to the job posting';
      case 'publish_job': return 'View published job';
      case 'invite_candidate': return 'View candidate';
      case 'request_temp': return 'View temp request';
      case 'update_application_status': return 'View applicant';
      case 'send_message': return 'View conversation';
      default: return 'Open';
    }
  })();

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>
        Proposed Action
      </div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{actionLabel}</div>
      {Object.entries(details).map(([key, value]) => (
        <div key={key} style={{ fontSize: 12, color: '#4a5568', marginBottom: 2 }}>
          <strong>{key.replace(/_/g, ' ')}:</strong> {String(value)}
        </div>
      ))}
      {status === 'pending' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              background: '#38a169',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '6px 16px',
              fontSize: 12,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </button>
          <button
            onClick={handleCancel}
            disabled={loading}
            style={{
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: 6,
              padding: '6px 16px',
              fontSize: 12,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}
      {status === 'executed' && (
        <div style={{ fontSize: 12, marginTop: 8 }}>
          <div style={{ color: '#38a169' }}>
            <i className="fas fa-check-circle" style={{ marginRight: 4 }} />
            {result || 'Completed'}
          </div>
          {redirectUrl && (
            <a href={redirectUrl} style={resultButtonStyle}>
              {resultButtonLabel}
              <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
            </a>
          )}
        </div>
      )}
      {status === 'cancelled' && (
        <div style={{ color: '#718096', fontSize: 12, marginTop: 8 }}>
          <i className="fas fa-ban" style={{ marginRight: 4 }} />
          Cancelled
        </div>
      )}
      {status === 'failed' && (
        <div style={{ fontSize: 12, marginTop: 8 }}>
          <div style={{ color: needsSubscription ? '#d69e2e' : '#e53e3e' }}>
            <i className={`fas fa-${needsSubscription ? 'exclamation-triangle' : 'exclamation-circle'}`} style={{ marginRight: 4 }} />
            {result || 'Failed'}
          </div>
          {redirectUrl && (
            <a href={redirectUrl} style={resultButtonStyle}>
              {resultButtonLabel}
              <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
            </a>
          )}
        </div>
      )}
    </div>
  );
};
